import { Api } from '@/api';
import type { ContactEventType } from '@/api/entity_types/ContactEvent';
import type { EventDuration, EventLocation, TeamEntity } from '@/api/entity_types/Team';
import type { TeamMemberEntity } from '@/api/entity_types/TeamMember';
import { useCurrentUserStore } from '@/stores/currentUser';
import { useTeamStore, type MemberAssignment } from '@/stores/team';
import { addMinutes } from 'date-fns';
import { computed, ref, watch } from 'vue';
import { toast } from 'vue-sonner';
import type { UseScheduleInterviewInputArgs } from './useScheduleInterviewInput';

type InterviewerOption = {
  value: string;
  label: string;
  email: string;
  assignments: MemberAssignment[];
};

const DURATION_MINUTES: Record<EventDuration, number> = {
  '15_minutes': 15,
  '30_minutes': 30,
  '45_minutes': 45,
  '60_minutes': 60,
};

export const locationOptions = [
  { value: 'phone_call', label: 'Phone Call' },
  { value: 'humaniz_conferencing', label: 'Humaniz Conferencing' },
  { value: 'standard_conferencing', label: 'Standard Conferencing' },
  { value: 'physical_location', label: 'Physical Location' },
] as const;

const teamLocationForEventType = (team: TeamEntity, eventType: ContactEventType): EventLocation => {
  switch (eventType) {
    case 'phone_screen':
      return team.phone_screen_event_location;
    case 'interview':
      return team.interview_event_location;
    case 'pipeline_review':
      return team.pipeline_review_event_location;
    default:
      return team.phone_screen_event_location;
  }
};

export const durationOptions = [
  { value: '15_minutes', label: '15 min' },
  { value: '30_minutes', label: '30 min' },
  { value: '45_minutes', label: '45 min' },
  { value: '60_minutes', label: '60 min' },
] as const;

export type UseAdminScheduleInputReturn = ReturnType<typeof useAdminScheduleInput>;

export const useAdminScheduleInput = ({ contact, event_type }: UseScheduleInterviewInputArgs) => {
  const teamStore = useTeamStore();
  const currentUserStore = useCurrentUserStore();

  const dateTime = ref('');
  const location = ref<EventLocation>(teamLocationForEventType(teamStore.team, event_type.value));
  const address = ref('');
  const duration = ref<EventDuration>('30_minutes');
  const interviewerId = ref<string>('');

  const hasDefaults = computed(() => !!teamStore.team.phone_screen_user_id || !!teamStore.team.interview_user_id);
  const isExpanded = computed(() => teamStore.calendarConnectionsLoaded || !hasDefaults.value);
  const isLoadingMore = computed(() => teamStore.isLoadingCalendarConnections);
  const interviewerOptions = computed<InterviewerOption[]>(() => {
    if (isExpanded.value) return teamStore.membersWithCalendar.map(memberToOption);
    const ids = [teamStore.team.phone_screen_user_id, teamStore.team.interview_user_id].filter(
      (id): id is number => !!id
    );
    const uniqueIds = Array.from(new Set(ids));
    return uniqueIds
      .map(id => teamStore.activeTeamMembers.find(member => member.id === id))
      .filter((member): member is TeamMemberEntity => !!member)
      .map(memberToOption);
  });

  if (isExpanded.value && !teamStore.calendarConnectionsLoaded) {
    teamStore.loadCalendarConnections();
  }

  const loadMoreInterviewers = () => teamStore.loadCalendarConnections();

  const memberToOption = (member: TeamMemberEntity): InterviewerOption => ({
    value: member.id.toString(),
    label: member.name,
    email: member.email,
    assignments: teamStore.getMemberAssignments(member.id),
  });

  const pickDefaultInterviewerId = (options: InterviewerOption[], eventType: ContactEventType): string => {
    const preferredId =
      eventType === 'phone_screen'
        ? teamStore.team.phone_screen_user_id
        : eventType === 'interview'
          ? teamStore.team.interview_user_id
          : null;
    const currentUserId = currentUserStore.currentUser.id?.toString() ?? '';
    return (
      options.find(o => o.value === preferredId?.toString())?.value ??
      options.find(o => o.value === currentUserId)?.value ??
      options[0]?.value ??
      ''
    );
  };

  const validate = (): boolean => {
    const errors: string[] = [];
    if (!interviewerId.value) errors.push('Interviewer is required');
    if (!dateTime.value) {
      errors.push('Date and time are required');
    } else {
      const startDate = new Date(dateTime.value);
      if (startDate <= new Date()) errors.push('Start time must be in the future');
    }
    if (location.value === 'physical_location' && !address.value.trim()) {
      errors.push('Address is required for physical locations');
    }
    if (errors.length) {
      toast.error('Validation Error', { description: errors.join(', ') });
      return false;
    }
    return true;
  };

  const submit = async () => {
    const startDate = new Date(dateTime.value);
    const endDate = addMinutes(startDate, DURATION_MINUTES[duration.value]);

    const response = await Api.Public.ContactInterview.createContactInterview({
      event_type: event_type.value,
      contact_id: contact.value.id.toString(),
      company_slug: teamStore.team.slug ?? '',
      start_time: startDate.toISOString(),
      end_time: endDate.toISOString(),
      contact: contact.value,
      scheduled_by: 'admin',
      location: location.value,
      address: address.value,
      interviewer_id: interviewerId.value ? Number(interviewerId.value) : undefined,
    });
    if (response.status === 'error') {
      const description = `The meeting was not scheduled: ${response.messages.join(', ')}`;
      toast.error('Scheduling Failed', { description });
    }
  };

  watch(
    event_type,
    eventType => {
      interviewerId.value = pickDefaultInterviewerId(interviewerOptions.value, eventType);
    },
    { immediate: true }
  );

  watch(interviewerOptions, options => {
    if (interviewerId.value && options.some(o => o.value === interviewerId.value)) return;
    interviewerId.value = pickDefaultInterviewerId(options, event_type.value);
  });

  return {
    dateTime,
    location,
    address,
    duration,
    interviewerId,
    interviewerOptions,
    locationOptions,
    durationOptions,
    hasDefaults,
    isExpanded,
    isLoadingMore,
    loadMoreInterviewers,
    validate,
    submit,
  };
};
